import type { Transport, TransportAssoc } from "@ots-frontend/ots-data";
import type { ActivityMarker } from "./activity-types";

/** The small icons beside a stop or a ticket: split, alerts, restrictions, disputes, compliance, coding… */
export const stopMarkers = (transport: Transport | undefined, multipart: boolean | undefined): ActivityMarker[] => {
  if (!transport) return [];
  const markers: ActivityMarker[] = [];
  if (multipart) markers.push({ id: "split", label: "Split load", tone: "info" });
  if (transport.triggeredAlerts?.length) {
    const count = transport.triggeredAlerts.length;
    markers.push({ id: "alert", label: `${count} alert${count === 1 ? "" : "s"} triggered at this tank`, tone: "danger", count });
  }
  if (transport.containerLactMeterAttached) markers.push({ id: "lact", label: "LACT attached", tone: "info" });
  if (transport.containerScadaMode === "DISABLED") markers.push({ id: "scada-off", label: "SCADA disabled for this tank", tone: "warning" });
  if (transport.wellStageIsDefault === false || transport.projectedWellStageIsDefault === false) markers.push({ id: "well-stage", label: "Non-default well stage", tone: "danger" });
  if (transport.restrictions?.length) {
    markers.push({
      id: "restriction",
      label: `Restriction active: ${transport.restrictions.map((restriction) => restriction.displayName ?? restriction.type).join(", ")}`,
      tone: "danger",
      count: transport.restrictions.length,
    });
  }
  if (transport.manualEntry) markers.push({ id: "manual", label: "Entered manually", tone: "neutral" });
  return markers;
};

export const ticketMarkers = (tta: TransportAssoc | undefined, pickup: Transport | undefined, delivery: Transport | undefined): ActivityMarker[] => {
  const markers: ActivityMarker[] = [];
  if (tta?.validationStatus === "DISPUTED" || tta?.disputed) markers.push({ id: "disputed", label: "Disputed", tone: "danger" });
  if (tta?.holdingRecord || pickup?.holdingRecord || delivery?.holdingRecord) markers.push({ id: "holding", label: "Holding record", tone: "neutral" });
  if (pickup?.rejectionType || delivery?.rejectionType) {
    markers.push({ id: "rejected", label: `Rejected${pickup?.rejectionComment ? `: ${pickup.rejectionComment}` : ""}`, tone: "danger" });
  }
  if (tta?.dispatches?.length || tta?.dispatchId) markers.push({ id: "dispatch-link", label: "Linked to a dispatch", tone: "info" });

  const compliance = tta?.rrcEp10WasteManifestBuildStatus ?? pickup?.rrcEp10WasteManifestBuildStatus;
  if (compliance?.status === "BLOCKED") markers.push({ id: "compliance", label: "Compliance blocked", tone: "danger" });
  else if (compliance?.status === "GENERATED") markers.push({ id: "compliance", label: "Compliance processed", tone: "success" });

  if (tta?.ticketCodeList?.length) {
    markers.push({ id: "coding", label: `Coded to ${tta.ticketCodeList.map((code) => code.afe ?? code.code ?? code.wellName).filter(Boolean).join(", ") || "an account"}`, tone: "violet", count: tta.ticketCodeList.length });
  }
  return markers;
};
